import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import { apiRequest } from "../../services/api";
const field = "mt-1 block w-full rounded-lg border p-2",
  button = "rounded-lg border px-3 py-2 disabled:opacity-40";
export default function PaymentMovementPanel({ companyId, debit, onUpdated }) {
  const { accessToken, user } = useAuth(),
    canPost = hasPermission(user, "accounting.vouchers_post"),
    canReverse = hasPermission(user, "accounting.vouchers_reverse");
  const [data, setData] = useState(null),
    [kind, setKind] = useState(canPost ? "DEBIT" : "REVERSAL"),
    [preview, setPreview] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const generation = useRef(0),
    working = useRef(false),
    retry = useRef(null);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const root = `/accounting/${companyId}/payment-movements`,
    request = (u, o = {}) => apiRequest(u, { accessToken, ...o });
  const clear = () => {
    setPreview(null);
    setChecks({});
  };
  async function run(work, done) {
    if (working.current) return;
    working.current = true;
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await work();
      if (g === generation.current) done(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) {
        working.current = false;
        setBusy(false);
      }
    }
  }
  const reviews = {
    bankReviewed:
      "I compared the actual retained bank movement and original payment.",
    priorEntriesReviewed:
      "I checked prior vouchers and accounting entries to avoid posting the same movement twice.",
    suspenseReviewed:
      "I accept temporary unresolved suspense treatment, not revenue/GST adjustment or a final expense.",
    noTransferConfirmed:
      "I understand this posts accounting only and sends no money or refund request.",
  };
  function save() {
    if (
      !preview ||
      busy ||
      !Object.keys(reviews).every((k) => checks[k]) ||
      (kind === "REVERSAL" ? !canReverse : !canPost)
    )
      return;
    const body = {
        ...preview.command,
        previewHash: preview.previewHash,
        ...checks,
      },
      signature = JSON.stringify(body);
    if (retry.current?.signature !== signature) {
      if (!window.crypto?.randomUUID) {
        setError("HTTPS or localhost is required for secure posting keys");
        return;
      }
      retry.current = { signature, key: window.crypto.randomUUID() };
    }
    body.requestKey = retry.current.key;
    run(
      async () => {
        const result = await request(
          kind === "REVERSAL" ? `${root}/reverse` : root,
          { method: "POST", body },
        );
        const refreshed = await request(`${root}/${debit.id}`);
        await onUpdated();
        return { result, refreshed };
      },
      ({ refreshed }) => {
        setData(refreshed);
        clear();
        setNotice(
          "Balanced source-linked voucher posted. No bank transfer, stock return or GST/revenue adjustment.",
        );
      },
    );
  }
  return (
    <details className="min-w-80">
      <summary className="cursor-pointer">Financial posting / suspense</summary>
      <div className="mt-3 space-y-3 rounded border p-3">
        <p>
          Post the observed bank movement to a reviewed dedicated asset suspense
          ledger. Final refund, expense, revenue and GST treatment stays
          unresolved.
        </p>
        {error && (
          <p role="alert" className="text-red-700">
            {error}
          </p>
        )}
        {notice && <p role="status">{notice}</p>}
        <button
          className={button}
          disabled={busy}
          onClick={() => run(() => request(`${root}/${debit.id}`), setData)}
        >
          Load financial posting workspace
        </button>
        {data && (
          <>
            <p>
              Source-linked suspense net: INR {data.sourceSuspenseNet}. This is
              not an approved final account balance.
            </p>
            <ol className="space-y-2">
              {data.rows.map((r) => (
                <li key={r.id}>
                  {" "}
                  {r.kind} · {r.voucher?.number} ·{" "}
                  {r.voucher?.date?.slice(0, 10)} · INR {r.amount} ·{" "}
                  {r.kind === "REVERSAL"
                    ? "Correction voucher"
                    : r.reversedBy
                      ? "Reversed, history retained"
                      : "Posted to unresolved suspense"}
                </li>
              ))}
            </ol>
            {(canPost || canReverse) && (
              <fieldset disabled={busy} className="space-y-3">
                <label className="block">
                  Financial action
                  <select
                    className={field}
                    value={kind}
                    onChange={(e) => {
                      clear();
                      setKind(e.target.value);
                    }}
                  >
                    {canPost && (
                      <>
                        <option value="DEBIT">
                          Post original debit to suspense
                        </option>
                        <option value="RECOVERY">
                          Post retained recovery credit
                        </option>
                      </>
                    )}
                    {canReverse && (
                      <option value="REVERSAL">
                        Reverse a posted payment movement
                      </option>
                    )}
                  </select>
                </label>
                <form
                  key={kind}
                  className="space-y-3"
                  onChange={clear}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const values = Object.fromEntries(
                        new FormData(e.currentTarget),
                      ),
                      command = {
                        ...values,
                        kind,
                        ...(kind === "DEBIT" ? { targetId: debit.id } : {}),
                      };
                    run(
                      () =>
                        request(`${root}/preview`, {
                          method: "POST",
                          body: command,
                        }),
                      (r) => setPreview({ ...r, command }),
                    );
                  }}
                >
                  {kind !== "DEBIT" && (
                    <label className="block">
                      Original recovery / posting
                      <select className={field} required name="targetId">
                        <option value="">Select original source</option>
                        {kind === "RECOVERY"
                          ? (debit.events || [])
                              .filter(
                                (e) => e.kind === "RECOVERY" && !e.voidedId,
                              )
                              .map((e) => (
                                <option key={e.id} value={e.id}>
                                  Recovery #{e.sequence} · INR {e.amount} ·{" "}
                                  {e.externalReference}
                                </option>
                              ))
                          : data.rows
                              .filter(
                                (r) => r.kind !== "REVERSAL" && !r.reversedBy,
                              )
                              .map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.kind} · {r.voucher?.number} · INR{" "}
                                  {r.amount}
                                </option>
                              ))}
                      </select>
                    </label>
                  )}
                  {kind === "DEBIT" && (
                    <label className="block">
                      Reviewed suspense asset ledger
                      <select className={field} required name="pendingLedgerId">
                        <option value="">
                          Select dedicated suspense (server validates)
                        </option>
                        {data.workspace.ledgers
                          .filter(
                            (l) =>
                              l.kind === "GENERAL" &&
                              l.group.nature === "ASSET",
                          )
                          .map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.code} · {l.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  <label className="block">
                    Posting financial year
                    <select className={field} name="yearId" required>
                      <option value="">Select year</option>
                      {data.workspace.years.map((y) => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    Financial posting date
                    <input className={field} name="date" type="date" required />
                  </label>
                  <label className="block">
                    Financial review / reversal reason
                    <textarea
                      className={field}
                      name="reviewNote"
                      minLength={20}
                      maxLength={1000}
                      required
                    />
                  </label>
                  <button className={button}>
                    Preview balanced financial posting
                  </button>
                </form>
                {preview && (
                  <div className="space-y-3 rounded bg-amber-50 p-3">
                    <p>{preview.warning}</p>
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr>
                          <th>Ledger</th>
                          <th>Debit INR</th>
                          <th>Credit INR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.payload.lines.map((l) => (
                          <tr key={l.ledgerId}>
                            <td>
                              {data.workspace.ledgers.find(
                                (x) => x.id === l.ledgerId,
                              )?.name || l.ledgerId}
                            </td>
                            <td>{l.debit}</td>
                            <td>{l.credit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {Object.entries(reviews).map(([key, label]) => (
                      <label key={key} className="block">
                        <input
                          type="checkbox"
                          checked={!!checks[key]}
                          onChange={(e) =>
                            setChecks((s) => ({
                              ...s,
                              [key]: e.target.checked,
                            }))
                          }
                        />{" "}
                        {label}
                      </label>
                    ))}
                    <button
                      className={button}
                      disabled={
                        !Object.keys(reviews).every((k) => checks[k]) ||
                        (kind === "REVERSAL" ? !canReverse : !canPost)
                      }
                      onClick={save}
                    >
                      Post reviewed source-linked voucher
                    </button>
                  </div>
                )}
              </fieldset>
            )}
          </>
        )}
      </div>
    </details>
  );
}
